import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { motion } from 'framer-motion';
import { ChevronUp, Download, Edit, Locate, MessageSquare } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as AILogoDark } from '@/assets/pngs/ai_generated_summary_dark.svg';
import { ReactComponent as AILogoLight } from '@/assets/pngs/ai_generated_summary_light.svg';
import { Button, Combobox, useToast } from '@/components';
import { useERSContext, useMapContext, useTheme } from '@/context';
import { useAIProvider } from '@/hooks/useAIProvider';
import { capitalizeWords, constants } from '@/lib';
import { flyToAndShowPin } from '@/pages/mapview/MapCommon';
import { handleDownloadPDF, INCIDENT_SUMMARIZATION_PROMPT, translateText } from '@/lib/ai/utils';
import { ResponderDetail } from '.';

// extend all plugins before using tz()
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

export const IncidentDetails = ({ data, setShowCreateIncident, onRowClick, cardData }) => {
  const { t, i18n } = useTranslation();
  const { mapRef, selectedIncident, unFocusIncident } = useMapContext();
  const { theme } = useTheme();
  const { chatHistory, setSelectedChatId, scrollToSelectedChat, incidentAssigneeStatusList, setIncidents } =
    useERSContext();
  const { error, callAPI } = useAIProvider();
  const { showToast } = useToast();

  const [summary, setSummary] = useState('');
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [localSeverity, setLocalSeverity] = useState(data.original?.severity);
  const [localStatus, setLocalStatus] = useState(data.original?.status);
  const [translate, setTranslate] = useState({
    incidentDescription: '',
    locationDescription: '',
  });

  // Sync local state when context updates from SDK callback
  useEffect(() => {
    setLocalSeverity(data.original?.severity);
  }, [data.original?.severity]);

  useEffect(() => {
    setLocalStatus(data.original?.status);
  }, [data.original?.status]);

  const handleRowToggleAndUnfocus = (clearSelection = true) => {
    const toggleHandler = data.getToggleExpandedHandler();
    toggleHandler();

    if (clearSelection && selectedIncident?.id === data.original.id) {
      unFocusIncident();
    }
  };

  const isResolved = data.original?.status === constants.TAB_KEYS.TAB_VALUE_RESOLVED;

  // Map display status values to SDK-expected values
  const normalizeStatus = status => (status === 'progress' ? 'in_progress' : status);

  const handleQuickUpdate = async (field, value) => {
    const sdk = window?.wbSdk?.incidentManager;
    if (!sdk?.updateIncident) {
      showToast({ type: constants?.MESSAGE?.ERROR, title: t('ERRORS.FAILED_TO_UPDATE_INCIDENT') });
      return;
    }

    const prev = field === 'severity' ? localSeverity : localStatus;
    if (field === 'severity') setLocalSeverity(value);
    else setLocalStatus(value);

    try {
      const apiValue = field === 'status' ? normalizeStatus(value) : value;
      const payload = {
        id: data.original.id,
        [field]: apiValue,
        lastUpdateTimestamp: data.original.lastUpdateTimestamp,
      };

      const updated = await sdk.updateIncident(payload);

      // Update incidents in context for immediate UI reflection
      setIncidents(prev => prev.map(inc => (inc.id === updated.id ? updated : inc)));

      showToast({
        type: constants?.MESSAGE?.INCIDENT,
        title: updated?.title,
        description: `${t('DASHBOARD.INCIDENT_UPDATED_BY')} ${updated?.updated_by?.display_name ?? ''}`,
      });
    } catch (err) {
      console.error(`Failed to update ${field}:`, err);
      // Revert on failure
      if (field === 'severity') setLocalSeverity(prev);
      else setLocalStatus(prev);
      showToast({ type: constants?.MESSAGE?.ERROR, title: t('ERRORS.FAILED_TO_UPDATE_INCIDENT') });
    }
  };

  useEffect(() => {
    if (!isResolved) return;

    if (!summary && !loadingSummary) {
      handleGenerateSummary(data.original);
    }

    const onLangChange = () => handleGenerateSummary(data.original);
    i18n.on('languageChanged', onLangChange);

    // cleanup on unmount
    return () => i18n.off('languageChanged', onLangChange);
  }, [isResolved]);

  useEffect(() => {
    if (onRowClick) {
      onRowClick(cardData);
    }
    const onLangChange = () => {
      handleTranslations();
    };

    // translate immediately on mount
    handleTranslations();

    i18n.on('languageChanged', onLangChange);
    return () => i18n.off('languageChanged', onLangChange);
  }, []);

  const handleGenerateSummary = async incidentOriginal => {
    setSummary('');
    setLoadingSummary(true);
    try {
      const matches = Object.keys(chatHistory).filter(k => k.startsWith(incidentOriginal?.id));
      const selectedChat = matches.length > 0 ? chatHistory[matches[0]] : null;
      if (selectedChat == null) throw new Error(t('ERRORS.INVALID_CHAT_DATA'));

      let currentLanguage = i18n.language;
      const tzName = dayjs.tz.guess(); // e.g. "Asia/Kolkata"
      //console.log('tzName: ', tzName);

      const userPrompt = `
        Incident Details:
        ${JSON.stringify(incidentOriginal, null, 2)}

        Chat History:
        ${JSON.stringify(selectedChat, null, 2)}

        Use the language: ${currentLanguage ? currentLanguage : 'en'}. Convert every UTC time into ${tzName ? tzName : 'UTC'} timezone time format.

        IMPORTANT: You MUST generate the final summary entirely in the language: ${currentLanguage}.
        If the requested language is Bahasa Indonesia (id) or Vietnamese (vi), you are STRICTLY required to produce the output in that language only. Do NOT fall back to English.
        ${
          currentLanguage === 'id'
            ? 'Contoh kalimat: Silakan buat ringkasan insiden berikut.'
            : currentLanguage === 'vi'
              ? 'Ví dụ câu: Vui lòng tạo bản tóm tắt sự cố sau đây.'
              : ''
        }
        Generate a 2-3 paragraph incident summary following all instructions above.
      `;

      const messages = [
        { role: 'system', content: INCIDENT_SUMMARIZATION_PROMPT },
        { role: 'user', content: userPrompt },
      ];

      const response = await callAPI(messages, {
        temperature: 0.3,
        maxTokens: 800,
      });

      setSummary(response.trim());
    } catch (error) {
      console.error(t('ERRORS.SUMMARY_GENERATION_FAILED'), error);
      setSummary(t('ERRORS.FAILED_TO_GENERATE_SUMMARY') + ' ' + t('ERRORS.PLEASE_TRY_AGAIN'));
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleTranslations = async () => {
    const incidentText = data.original?.description || '';
    const locationText = data.original?.location?.location_description || '';

    if (incidentText.trim() === '' && locationText.trim() === '') {
      setTranslate({ incidentDescription: '-', locationDescription: '-' });
      return;
    }

    if (i18n.language === 'en') {
      setTranslate({ incidentDescription: incidentText, locationDescription: locationText });
      return;
    }

    try {
      const translatedIncidentText = await translateText({
        callAPI,
        text: incidentText,
        language: i18n.language,
      });

      const translatedLocationText = await translateText({
        callAPI,
        text: locationText,
        language: i18n.language,
      });

      // console.log('Translated Descriptions: ', translatedIncidentText, translatedLocationText);
      setTranslate({ incidentDescription: translatedIncidentText, locationDescription: translatedLocationText });
    } catch (error) {
      console.error('Translation Error: ', error);
      setTranslate({ incidentDescription: text, locationDescription: locationText });
    }
  };

  // Use context function for chat selection with automatic scrolling
  const handleChatClick = () => {
    setSelectedChatId(cardData.id);

    // Call the scroll function after a small delay to ensure the chat is selected
    setTimeout(() => {
      if (scrollToSelectedChat) {
        scrollToSelectedChat();
      }
    }, 100);

    if (onRowClick) {
      onRowClick(cardData);
    }
  };

  return (
    <motion.div
      key="expanded"
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className="overflow-hidden"
    >
      <div className="rounded-lg shadow-sm bg text-sm">
        <div className="flex flex-row justify-between bg-subtle border-b border-border rounded-tl-lg rounded-tr-lg">
          <span
            className="cursor-pointer px-3 py-2 font-medium flex flex-1"
            onClick={() => handleRowToggleAndUnfocus()}
          >
            {data?.original?.title}
          </span>
          <div className="flex flex-row align-middle">
            {isResolved ? (
              <Button
                variant="ghost"
                size="ghostSmall"
                className="group"
                onClick={() => handleDownloadPDF(summary, selectedIncident)}
              >
                {!loadingSummary && (
                  <Download size={10} className="transition-transform duration-200 group-hover:scale-110" />
                )}
              </Button>
            ) : (
              <div className="flex flex-row align-middle border-l">
                <Button
                  variant="ghost"
                  size="ghostSmall"
                  className="group"
                  onClick={() => {
                    setShowCreateIncident(true);
                    unFocusIncident();
                    handleRowToggleAndUnfocus();
                  }}
                >
                  <Edit size={13} className="transition-transform duration-200 group-hover:scale-110" />
                </Button>
                <Button variant="ghost" size="ghostSmall" className="group" onClick={handleChatClick}>
                  <MessageSquare size={10} className="transition-transform duration-200 group-hover:scale-110" />
                </Button>
                <Button
                  variant="ghost"
                  size="ghostSmall"
                  className="group"
                  onClick={() => {
                    flyToAndShowPin(data.original.geoloc.lat, data.original.geoloc.long, 14, mapRef.current);
                    onRowClick(cardData);
                  }}
                >
                  <Locate size={10} className="transition-transform duration-200 group-hover:scale-110" />
                </Button>
              </div>
            )}
            <Button
              variant="ghost"
              size="ghostSmall"
              className="group border-l rounded-tr-lg"
              onClick={() => handleRowToggleAndUnfocus()}
            >
              <motion.div
                animate={{ rotate: data.getIsExpanded() ? 0 : 180 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <ChevronUp size={10} className="transition-transform duration-200 group-hover:scale-110" />
              </motion.div>
            </Button>
          </div>
        </div>
        <div className="flex flex-col gap-2 p-3">
          {isResolved && (
            <div className="flex flex-col gap-3">
              {theme === 'dark' ? <AILogoDark title="dark-mode-icon" /> : <AILogoLight title="light-mode-icon" />}
              <div className="flex flex-1 flex-col">
                <span className="font-medium">{capitalizeWords(t('DASHBOARD.SUMMARY'))}</span>
                {loadingSummary ? (
                  <span className="text-muted italic">{capitalizeWords(t('DASHBOARD.GENERATING_SUMMARY'))}</span>
                ) : (
                  <span className="text-muted whitespace-pre-wrap">{summary}</span>
                )}
              </div>
            </div>
          )}
          {isResolved && <div className="border-b"></div>}
          <div className="flex flex-row gap-4">
            <div className="flex flex-1 flex-col">
              <span className="font-medium">{t('INCIDENTS_PANEL.REPORTED_DATE_TIME')}</span>
              <span className="text-muted">
                {dayjs(data?.original?.creationTimestamp * 1000).format('YYYY-MM-DD HH:mm:ss')}
              </span>
            </div>
            <div className="flex flex-1 flex-col">
              <span className="font-medium">{t('INCIDENTS_PANEL.REPORTED_NAME')}</span>
              <span className="text-muted">
                {data.original.created_by.user_id != '' || data.original.created_by.user_id != undefined
                  ? data.original.created_by.display_name != ''
                    ? data.original.created_by.display_name
                    : '-'
                  : '-'}
              </span>
            </div>
          </div>
          <div className="flex flex-row gap-4">
            <div className="flex flex-1 flex-col">
              <span className="font-medium">{capitalizeWords(t('INCIDENTS_PANEL.DESCRIPTION'))}</span>
              <span className="text-muted">{translate.incidentDescription || data.original.description}</span>
            </div>
          </div>
          <div className="flex gap-4">
            <span className="font-medium">{capitalizeWords(t('INCIDENTS_PANEL.INCIDENT_TYPE'))}</span>
            <span className="text-muted">{data.original.type !== '' && data.original.type}</span>
          </div>
          <div className="flex flex-row gap-4">
            <div className="flex flex-1 flex-col">
              <span className="font-medium">{capitalizeWords(t('INCIDENTS_PANEL.LOCATION'))}</span>
              <span className="text-muted">
                {data.original?.location?.display_name ? data.original.location.display_name : '-'}
              </span>
            </div>
            <div className="flex flex-1 flex-col">
              <span className="font-medium">{capitalizeWords(t('INCIDENTS_PANEL.LOCATION_DESCRIPTION'))}</span>
              <span className="text-muted">
                {translate.locationDescription || data.original?.location?.location_description?.trim()}
              </span>
            </div>
          </div>
          <div className="flex flex-row gap-4">
            <div className="flex flex-1 flex-col gap-1">
              <span className="font-medium">{capitalizeWords(t('INCIDENTS_PANEL.SEVERITY'))}</span>
              <span className="text-muted">
                {isResolved ? (
                  t('SEVERITY_VALUES', { returnObjects: true }).find(item => item.value === data.original.severity)
                    .label
                ) : (
                  <Combobox
                    data={t('SEVERITY_VALUES', { returnObjects: true })}
                    selectedData={t('SEVERITY_VALUES', { returnObjects: true }).find(
                      item => item.value === localSeverity
                    )}
                    onChange={selected => selected && handleQuickUpdate('severity', selected.value)}
                    placeholder={t('PLACEHOLDER.SELECT_OPTION')}
                    dropUp
                  />
                )}
              </span>
            </div>
            <div className="flex flex-1 flex-col gap-1">
              <span className="font-medium">{capitalizeWords(t('INCIDENTS_PANEL.STATUS'))}</span>
              <span className="text-muted">
                {isResolved ? (
                  t('INCIDENT_DETAILS_STATUS_VALUES', { returnObjects: true }).find(
                    item => item.value === data.original.status
                  ).label
                ) : (
                  <Combobox
                    data={t('INCIDENT_DETAILS_STATUS_VALUES', { returnObjects: true })
                      .filter(item => item.value !== 'created')
                      .map(({ disabled, ...rest }) => rest)}
                    selectedData={t('INCIDENT_DETAILS_STATUS_VALUES', { returnObjects: true }).find(
                      item => item.value === localStatus
                    )}
                    onChange={selected => selected && handleQuickUpdate('status', selected.value)}
                    dropUp
                    placeholder={t('PLACEHOLDER.SELECT_OPTION')}
                  />
                )}
              </span>
            </div>
          </div>
          <div className="flex flex-row gap-4">
            <div className="flex flex-1 flex-col gap-1">
              <span className="font-medium">{capitalizeWords(t('INCIDENTS_PANEL.RESPONDERS'))}</span>
              <span className="grid gap-2 grid-cols-[repeat(2,minmax(0,1fr))] text-muted">
                {data.original.assignees.length > 0 ? (
                  data.original.assignees.map(item => (
                    <ResponderDetail
                      key={item.id}
                      assignee={item}
                      mode="view"
                      statusList={incidentAssigneeStatusList}
                    />
                  ))
                ) : (
                  <span className="text-muted">{t('INCIDENTS_PANEL.NO_RESPONDERS_ASSIGNED')}</span>
                )}
              </span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};
